import { useMemo, useState } from "react";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse, Card, UncontrolledTooltip } from "reactstrap";
import DOMPurify from "dompurify";

import styles from "Pages/Run/Components/MessageDetail/MessageDetail.module.css";

// DOMPurify's default allow-list keeps href but DROPS target, so the target="_blank"
// that useHelpers' convertLinks() puts on every link was being stripped and links opened
// in the same tab instead of a new one. rel is kept by default, so noopener/noreferrer
// survives. href sanitizing is unaffected -- javascript: URLs and on* handlers are still
// removed. Module-level so the useMemo deps below stay stable across renders.
const sanitizeOptions = { ADD_ATTR: ["target"] };

interface IProps {
    showAllDetails: boolean;
    firstPart: string;
    details: string;
}

// Used inside RunDetail.tsx to show the details of a message
export default function MessageDetail({
    showAllDetails,
    firstPart,
    details,
}: IProps) {
    const [showThisDetail, setShowThisDetail] = useState<boolean | null>(null);

    // Both props are rendered with dangerouslySetInnerHTML because useHelpers'
    // convertLinks() turns bare URLs in the text into real <a> tags. The text itself is
    // integration message content, which routinely embeds ERP/Procore data (project,
    // vendor and error strings) that we do not control -- so it is untrusted and must be
    // sanitized before it reaches the DOM. Same DOMPurify pattern as IssueDetail.tsx;
    // DOMPurify keeps <a href> by default, so the links still work.
    const sanitizedFirstPart = useMemo(
        () => DOMPurify.sanitize(firstPart, sanitizeOptions),
        [firstPart]
    );
    const sanitizedDetails = useMemo(
        () => DOMPurify.sanitize(details, sanitizeOptions),
        [details]
    );

    function toggleCollapse(e: React.MouseEvent) {
        // Check if the click was initiated from an a tag
        const target = e.target as HTMLElement;
        if (target.tagName === "A") {
            return;
        }

        switch (showThisDetail) {
            case null:
                setShowThisDetail(!showAllDetails);
                break;
            case false:
                setShowThisDetail(true);
                break;
            case true:
                setShowThisDetail(false);
                break;
            default:
                setShowThisDetail(null);
                break;
        }
    }

    function isCollapseOpen() {
        if (showThisDetail === null) {
            return showAllDetails;
        }
        return showThisDetail;
    }

    return (
        <div className={styles.detailsBox}>
            <div
                className={styles.details}
                dangerouslySetInnerHTML={{
                    // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml -- value is DOMPurify.sanitize()'d above; the rule flags the sink unconditionally and cannot see the sanitizer.
                    __html: sanitizedFirstPart,
                }}
            />

            <span>
                <button
                    id="expandButton"
                    type="button"
                    className={styles.expandButton}
                    onClick={toggleCollapse}
                >
                    <FontAwesomeIcon
                        className={styles.expandIcon}
                        icon={isCollapseOpen() ? faCaretUp : faCaretDown}
                    />
                </button>
                <UncontrolledTooltip target="expandButton">
                    {isCollapseOpen() ? "Collapse details" : "Expand details"}
                </UncontrolledTooltip>
            </span>

            <Collapse className={styles.collapse} isOpen={isCollapseOpen()}>
                <Card className={styles.collapseCard}>
                    <pre
                        className={styles.expandableDetails}
                        dangerouslySetInnerHTML={{
                            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml -- value is DOMPurify.sanitize()'d above; the rule flags the sink unconditionally and cannot see the sanitizer.
                            __html: sanitizedDetails,
                        }}
                    />
                </Card>
            </Collapse>
        </div>
    );
}
